import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, faqSchema } from "@/lib/firm";
import { SELLER_DISCLOSURE_START_LABEL } from "@/lib/seller-disclosure";

const FAQS = [
  {
    q: "What is a Form 2 seller disclosure statement in Queensland?",
    a: `Form 2 is the approved seller disclosure statement under the Property Law Act 2023. The seller disclosure scheme commenced on ${SELLER_DISCLOSURE_START_LABEL}. For sales covered by the scheme, the seller must give the buyer a completed Form 2 and the prescribed certificates before the buyer signs the contract. Exceptions apply, so the requirements should be checked for the particular sale.`,
  },
  {
    q: "What has to be attached to the Form 2?",
    a: "The prescribed certificates depend on the property, but commonly include a title search and registered plan, a copy of any registered easement, covenant or statutory encumbrance, a rates or water notice, a pool safety certificate or notice of no pool safety certificate, a transport infrastructure notice where relevant, and — for lots in a community titles scheme — a body corporate certificate and community management statement.",
  },
  {
    q: "What happens if the seller disclosure statement is wrong or late?",
    a: "If the statement is not given before the buyer signs, or it is materially inaccurate or incomplete and the buyer would not have signed had they known, the buyer may terminate the contract at any time up to settlement and have the deposit refunded. That is a very serious remedy for a paperwork failure, which is why the Form 2 is worth getting a solicitor to prepare.",
  },
  {
    q: "Does the agent or the seller prepare the Form 2?",
    a: "The obligation sits with the seller. In practice many agents order it on the seller's behalf so the listing can go to market with a compliant disclosure pack ready. We prepare Form 2 packs for both sellers directly and for agencies who want the job done by a solicitor rather than assembled in-house.",
  },
  {
    q: "How much does a Form 2 cost and how fast can you turn it around?",
    a: `We quote a fixed fee for a Form 2 disclosure pack, including the searches and certificates, before we start. Standard freehold lots are usually ready within one to two business days of instructions; community titles lots depend on how quickly the body corporate manager issues the certificate. Call ${FIRM.phone} for a figure on your listing.`,
  },
  {
    q: "Do I still need a Form 2 for an auction or an off-the-plan sale?",
    a: "Auctions and some off-the-plan and related-party transactions have their own treatment, and a small number of transactions are excluded from the disclosure regime altogether. Do not assume an exclusion applies — the cost of being wrong is a buyer walking away with their deposit at settlement. We check the position before the listing goes live.",
  },
];

const INCLUDED = [
  "Completed approved Form 2 seller disclosure statement",
  "Title search and registered plan of survey",
  "Registered easements, covenants and statutory encumbrances",
  "Local council rates and water notices",
  "Pool safety certificate or notice of no pool safety certificate",
  "Transport, contaminated land and other prescribed notices where relevant",
  "Body corporate certificate and CMS for community titles lots",
  "Solicitor review of the completed pack before it is given to buyers",
];

export const Route = createFileRoute("/seller-disclosure")({
  head: () => ({
    meta: [
      { title: fitTitle(`Seller Disclosure Statement QLD (Form 2) | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Seller disclosure statement QLD: what Form 2 must include, common mistakes that let buyers terminate, and a solicitor-prepared pack at a fixed fee."),
      },
      { property: "og:title", content: fitTitle(`Seller Disclosure Statement QLD (Form 2) | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Form 2 disclosure packs prepared and checked by a solicitor before your listing goes to market."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/seller-disclosure" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/seller-disclosure" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: SellerDisclosurePage,
});

function SellerDisclosurePage() {
  return (
    <>
      <PageHero
        eyebrow="Conveyancing · agent services"
        title="Form 2 seller disclosure QLD: prepared by a solicitor"
        cta="Order a Form 2 disclosure pack"
        lead={`Since ${SELLER_DISCLOSURE_START_LABEL}, most Queensland property sales require a completed Form 2 seller disclosure statement and prescribed certificates before the buyer signs. Missing or defective disclosure can give the buyer a right to terminate before settlement. We prepare and check the pack for sellers and real estate agencies, with a fixed fee quoted before we start.`}
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">What a compliant disclosure pack contains</h2>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {INCLUDED.map((item) => (
            <li
              key={item}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              {item}
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Seller disclosure statement QLD: common mistakes</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          These are the errors that most often put a sale at risk:
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-6 text-base leading-relaxed text-muted-foreground">
          <li>Giving the buyer the statement after they sign, or attaching it to the contract without actually handing it over beforehand</li>
          <li>Missing a registered easement or covenant shown on the title</li>
          <li>Using out-of-date rates or body corporate information</li>
          <li>Ticking "no pool" where there is a pool or spa, or leaving out the pool safety notice</li>
          <li>Not disclosing notices or orders the seller has received from council or government</li>
          <li>Signing the statement without having read the attached certificates</li>
        </ul>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Buying rather than selling? Check how you will hold the property:{" "}
          <Link to="/guides/$slug" params={{ slug: "joint-tenants-vs-tenants-in-common-queensland" }} className="font-semibold text-primary underline">joint tenants vs tenants in common</Link>.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Does Form 2 apply to commercial property?</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          The scheme covers sales of residential property, commercial property and vacant freehold
          land, subject to exceptions. The disclosure documents depend on the property and transaction.
          A shop, office or industrial property may need different checks from a home or unit.
        </p>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          See our <Link to="/commercial-conveyancing" className="font-semibold text-primary underline">commercial conveyancing service</Link>{" "}
          or check the <a href="https://www.qld.gov.au/housing/buying-owning-home/seller-disclosure-scheme" className="font-semibold text-primary underline">Queensland Government seller disclosure guide</a>.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">For real estate agencies</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Agencies increasingly want the disclosure pack ordered the day the listing authority is
          signed, so the property can go live with a compliant Form 2 attached to the contract. We take
          instructions directly from the listing agent with the seller's authority, order and review the
          searches, complete the Form 2, and send it back to the office ready to issue — usually within
          one to two business days for a standard freehold lot.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Because the statement is prepared and checked by a solicitor, the agency is not the one
          assembling statutory disclosure in-house and carrying the risk of an incomplete pack. We also
          handle{" "}
          <Link to="/aml-real-estate-agents" className="font-semibold text-primary underline">
            AML customer due diligence under a reliance agreement
          </Link>{" "}
          for agencies bringing their AML/CTF obligations online.
        </p>

        <div className="mt-16 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Selling without an agent?</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            The disclosure obligation is the same whether or not an agent is involved — and private
            sellers are the ones most likely to be caught out by it.
          </p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
            <Link
              to="/guides/$slug"
              params={{ slug: "how-do-i-sell-my-property-privately-queensland" }}
              className="text-primary underline"
            >
              How do I sell my property privately in Queensland?
            </Link>
            <Link to="/conveyancing" className="text-primary underline">
              Fixed-fee conveyancing
            </Link>
          </div>
        </div>

        <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} heading="Who prepares your disclosure pack" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Questions sellers and agents ask about Form 2
        </h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-16">
          <EnquiryForm heading="Order a Form 2 disclosure pack" />
        </div>
      </section>

      <CtaBand
        title="Listing about to go live?"
        body={`Send us the address and we will come back with a fixed fee and a turnaround time, or call ${FIRM.phone}.`}
      />
      <div className="h-20" />
    </>
  );
}
